import React, { useState, useEffect } from 'react';
import './AtestadoMedicoForm.css';
import { PDFDownloadLink } from '@react-pdf/renderer';
import AtestadoMedicoPDF from './AtestadoMedicoPDF';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CidAutocomplete from '../CidAutocomplete/CidAutocomplete';
import Checklist from '../ChecklistItems ';
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

const AtestadoMedicoForm = () => {
  const [formData, setFormData] = useState({
    nomePaciente: '',
    dataEmissao: '',
    cid: '',
    diasAfastamento: '',
    assinaturaDigital: '',
    observacoes: '',
    localAtendimento: '',
    nomeMedico: '',
    crmMedico: '',
    estadoCRM: '',
  });
  const [pacientes, setPacientes] = useState([]);
  const [clinicas, setClinicas] = useState([]);
  const [isFormValid, setIsFormValid] = useState(false);

  // Função para obter a data e hora atual no formato correto
  const getCurrentDateTime = () => {
    const now = new Date();
    return now.toISOString().slice(0, 16); // Formato 'YYYY-MM-DDTHH:mm'
  };

  useEffect(() => {
    const db = getFirestore();
    const auth = getAuth();
    const user = auth.currentUser;

    // Definir a data de emissão como a data atual no carregamento do componente
    setFormData((prevData) => ({
      ...prevData,
      dataEmissao: getCurrentDateTime(),
    }));

    const fetchUserData = async () => {
      if (user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setFormData((prevData) => ({
            ...prevData,
            nomeMedico: `${userData.titulo} ${userData.nome}`,
            crmMedico: userData.crm || '',
            estadoCRM: userData.estadoCrm || '',
            assinaturaDigital: userData.nome || '',
          }));
        }
      }
    };

    const fetchPacientes = async () => {
      if (user) {
        const pacientesQuery = query(
          collection(db, 'pacientes'),
          where('userId', '==', user.uid)
        );
        const pacientesSnapshot = await getDocs(pacientesQuery);
        setPacientes(pacientesSnapshot.docs.map((doc) => doc.data().nome));
      }
    };

    const fetchClinicas = async () => {
      if (user) {
        const clinicasQuery = query(
          collection(db, 'clinicas'),
          where('userId', '==', user.uid)
        );
        const clinicasSnapshot = await getDocs(clinicasQuery);
        setClinicas(clinicasSnapshot.docs.map((doc) => doc.data().nome));
      }
    };

    fetchUserData();
    fetchPacientes();
    fetchClinicas();
  }, []);

  // Form Validation
  useEffect(() => {
    const isValid =
      formData.nomePaciente &&
      formData.dataEmissao &&
      formData.cid &&
      formData.diasAfastamento &&
      formData.assinaturaDigital &&
      formData.localAtendimento &&
      formData.nomeMedico &&
      formData.crmMedico &&
      formData.estadoCRM;
    setIsFormValid(isValid);
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  return (
    <div>
      <h2 id="atestado-form-heading" className="font-bold mb-4">
        Gerar Atestado Médico
      </h2>
      <form
        className="atestado-medico-form"
        aria-labelledby="atestado-form-heading"
      >
        <div className="form-section">
          <h3 className="section-title">Informações do Paciente</h3>

          <div className="input-group">
            <label htmlFor="nomePaciente">Nome do Paciente</label>
            <Autocomplete
              freeSolo
              id="paciente-autocomplete"
              options={pacientes}
              value={formData.nomePaciente}
              onInputChange={(e, newValue) =>
                setFormData((prevData) => ({
                  ...prevData,
                  nomePaciente: newValue, // Preenchimento manual ou via autocomplete
                }))
              }
              onChange={(e, newValue) =>
                setFormData((prevData) => ({
                  ...prevData,
                  nomePaciente: newValue || e.target.value, // Aceita manual ou autocomplete
                }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  id="nomePaciente"
                  name="nomePaciente"
                  placeholder="Nome do Paciente"
                  variant="outlined"
                  required
                  onChange={handleChange} // Preenchimento manual
                />
              )}
            />
          </div>

          <div className="input-group">
            <label htmlFor="localAtendimento">Local de Atendimento</label>
            <Autocomplete
              freeSolo
              id="clinica-autocomplete"
              options={clinicas}
              value={formData.localAtendimento}
              onInputChange={(e, newValue) =>
                setFormData((prevData) => ({
                  ...prevData,
                  localAtendimento: newValue,
                }))
              }
              onChange={(e, newValue) =>
                setFormData((prevData) => ({
                  ...prevData,
                  localAtendimento: newValue || e.target.value,
                }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  id="localAtendimento"
                  name="localAtendimento"
                  placeholder="Clínica de Atendimento"
                  variant="outlined"
                  required
                  onChange={handleChange}
                />
              )}
            />
          </div>

          <div className="input-group">
            <label htmlFor="cid">Código CID</label>
            <CidAutocomplete
              value={formData.cid || ''}
              onChange={(newCid) =>
                handleChange({ target: { name: 'cid', value: newCid } })
              }
            />
          </div>

          <div className="input-group">
            <label htmlFor="diasAfastamento">
              Período de Afastamento (dias)
            </label>
            <input
              type="number"
              id="diasAfastamento"
              name="diasAfastamento"
              value={formData.diasAfastamento}
              onChange={handleChange}
              required
              min="1"
              max="365"
              placeholder="Número de dias"
            />
          </div>

          <div className="input-group">
            <label htmlFor="dataEmissao">Data de Emissão</label>
            <input
              type="datetime-local"
              id="dataEmissao"
              name="dataEmissao"
              value={formData.dataEmissao}
              onChange={handleChange}
              required
              className="w-full border rounded-md p-2"
              readOnly
            />
          </div>
        </div>

        <div className="mt-4">
          <Checklist formData={formData} />
        </div>

        <PDFDownloadLink
          document={<AtestadoMedicoPDF data={formData} />}
          fileName="Atestado_Medico.pdf"
        >
          {({ loading }) => (
            <button
              type="button"
              className="px-4 py-2 rounded-md shadow-md text-base font-medium transition transform duration-300 ease-in-out focus:outline-none focus:ring-4 focus:ring-opacity-50 active:scale-95 bg-blue-600 text-white font-bold hover:bg-blue-700"
              aria-label="Gerar atestado médico"
              disabled={!isFormValid || loading}
            >
              {loading ? 'Gerando PDF...' : 'Gerar Atestado Médico'}
            </button>
          )}
        </PDFDownloadLink>
      </form>
    </div>
  );
};

export default AtestadoMedicoForm;
